import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation, gql } from '@apollo/client';

const TRIGGER_SCHEDULED_TASK = gql`
  mutation TriggerScheduledTask($taskType: String!, $orgId: Long) {
    triggerScheduledTask(taskType: $taskType, orgId: $orgId) {
      success
      runId
      message
    }
  }
`;

const taskTypes = [
  {
    value: 'kpiCoordinator',
    label: 'KPI Coordinator',
    requiresOrg: false,
    description: 'Invoke the nightly rollup coordinator Lambda',
  },
  {
    value: 'executiveWeekly',
    label: 'Executive Weekly Report',
    requiresOrg: true,
    description: 'Send weekly executive KPI report for an org',
  },
  {
    value: 'executiveMonthly',
    label: 'Executive Monthly Report',
    requiresOrg: true,
    description: 'Send monthly executive KPI report for an org',
  },
  {
    value: 'orScheduleDaily',
    label: 'OR Schedule Daily Report',
    requiresOrg: true,
    description: 'Send daily OR schedule report for an org',
  },
];

const SchedulerTools = () => {
  const [selectedTask, setSelectedTask] = useState(taskTypes[0].value);
  const [orgId, setOrgId] = useState('');
  const [results, setResults] = useState<
    Array<{ taskType: string; success: boolean; message: string; timestamp: string }>
  >([]);

  const [triggerTask, { loading }] = useMutation(TRIGGER_SCHEDULED_TASK);

  const currentTask = taskTypes.find(t => t.value === selectedTask);
  const needsOrg = currentTask?.requiresOrg ?? false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (needsOrg && !orgId.trim()) return;

    try {
      const variables: { taskType: string; orgId?: number } = { taskType: selectedTask };
      if (needsOrg && orgId.trim()) {
        variables.orgId = parseInt(orgId.trim(), 10);
      }

      const { data } = await triggerTask({ variables });
      const result = data?.triggerScheduledTask;

      setResults(prev => [
        {
          taskType: selectedTask,
          success: result?.success ?? false,
          message: result?.message ?? 'No response',
          timestamp: new Date().toLocaleTimeString(),
        },
        ...prev,
      ]);
    } catch (err: any) {
      setResults(prev => [
        {
          taskType: selectedTask,
          success: false,
          message: err.message || 'Request failed',
          timestamp: new Date().toLocaleTimeString(),
        },
        ...prev,
      ]);
    }
  };

  return (
    <Container>
      <Title>Scheduler Task Trigger</Title>
      <Subtitle>
        Invoke scheduled tasks on demand using the same code path as the scheduler. Not available in production.
      </Subtitle>

      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label>Task Type</Label>
          <Select value={selectedTask} onChange={e => setSelectedTask(e.target.value)}>
            {taskTypes.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          {currentTask && <HelpText>{currentTask.description}</HelpText>}
        </FieldGroup>

        {needsOrg && (
          <FieldGroup>
            <Label>Organization ID</Label>
            <Input
              type="number"
              value={orgId}
              onChange={e => setOrgId(e.target.value)}
              placeholder="e.g. 42"
              required
            />
          </FieldGroup>
        )}

        <Button type="submit" disabled={loading || (needsOrg && !orgId.trim())}>
          {loading ? 'Invoking...' : 'Trigger Task'}
        </Button>
      </Form>

      {results.length > 0 && (
        <ResultsSection>
          <Label>Results</Label>
          {results.map((r, i) => (
            <ResultEntry key={i} $success={r.success}>
              <ResultHeader>
                <ResultBadge $success={r.success}>{r.success ? 'OK' : 'FAIL'}</ResultBadge>
                <ResultTask>{taskTypes.find(t => t.value === r.taskType)?.label ?? r.taskType}</ResultTask>
                <ResultTime>{r.timestamp}</ResultTime>
              </ResultHeader>
              <ResultMessage>{r.message}</ResultMessage>
            </ResultEntry>
          ))}
        </ResultsSection>
      )}
    </Container>
  );
};

const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HelpText = styled.span`
  font-size: 0.8rem;
  color: #666;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #e0e0e0;
  font-size: 0.9rem;
  &:focus {
    outline: none;
    border-color: #00a7f7;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #e0e0e0;
  font-size: 0.9rem;
  &:focus {
    outline: none;
    border-color: #00a7f7;
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: #00a7f7;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms;
  &:hover:not(:disabled) {
    background: #0090d4;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResultsSection = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResultEntry = styled.div<{ $success: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${p => (p.$success ? '#2d4a2d' : '#4a2d2d')};
  border-radius: 8px;
  background: ${p => (p.$success ? '#1a2e1a' : '#2e1a1a')};
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const ResultBadge = styled.span<{ $success: boolean }>`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${p => (p.$success ? '#2d6a2d' : '#6a2d2d')};
  color: #fff;
`;

const ResultTask = styled.span`
  font-size: 0.85rem;
  color: #ccc;
  font-weight: 500;
`;

const ResultTime = styled.span`
  font-size: 0.75rem;
  color: #666;
  margin-left: auto;
`;

const ResultMessage = styled.pre`
  font-size: 0.8rem;
  color: #999;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

export default SchedulerTools;
